// ImpersonationContext — combines two impersonation modes:
//
//   1. session-only "preview" (sessionStorage flag + banner — RLS
//      stays bound to the admin's real user_id)
//   2. JWT-mint impersonation via the admin-impersonate edge function
//      (returns a magic-link the admin can open in a separate browser
//      tab — RLS becomes the target user's)
//
// ─── SECURITY MODEL ────────────────────────────────────────────────────
// Mode 1 (preview) is the default. It NEVER swaps the admin's session.
// Mode 2 (mintImpersonationLink) is opt-in and explicit — the call
// returns a magic-link URL but does NOT replace the current session.
// Opening that URL is a deliberate action the admin must take, in a
// separate tab/container. The edge function refuses to impersonate
// another sysadmin and is rate-limited to 5 mints / 5 min.
//
// What this *does* provide:
//   - A `viewAsUserId` slot stored in sessionStorage (cleared with the
//     tab — never persisted across browser restarts).
//   - A persistent banner at the top of every page that makes the
//     impersonation state impossible to forget.
//   - A hook (`useImpersonation`) that specific screens can read so
//     they can preview content as the target user *where the admin is
//     already permitted to do so* (e.g., reading the target's
//     workflows via an admin-scoped query).
//   - A best-effort audit-emit when impersonation starts and stops,
//     written to the admin_audit_log table.
//
// What this explicitly does NOT do:
//   - Mint a new JWT or call setSession.
//   - Bypass RLS or any tier-gate.
//   - Allow the admin to perform writes-as-the-user. Any write still
//     runs through the admin's RLS context.
//
// Why this stub: a full JWT-mint impersonation requires SECURITY
// DEFINER server-side functions, careful session-token revocation, and
// a security review. That work has its own PR. This stub gives admins
// a *navigable preview* + an audit trail today, while leaving the
// surface for the real impl to plug in later.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { callRpc } from '@/lib/supabaseTyped';
import { supabase } from '@/integrations/supabase/client';

const STORAGE_KEY = 'fw.impersonation.viewAsUserId';
const LABEL_KEY = 'fw.impersonation.label';

interface ImpersonationValue {
  /** Currently impersonated user id, or null. */
  viewAsUserId: string | null;
  /** Human label for the banner (usually email). */
  label: string | null;
  /** Begin session-only impersonation. Emits an admin_audit_log entry. */
  start: (userId: string, label: string | null) => Promise<void>;
  /** End session-only impersonation. Also emits an audit entry. */
  stop: () => Promise<void>;
  /** Request a JWT-mint impersonation magic-link. Returns the link
   *  URL on success — caller is responsible for opening it in a
   *  separate tab. Does NOT replace the current session. */
  mintImpersonationLink: (
    userId: string,
    opts?: { reason?: string; redirectTo?: string },
  ) => Promise<MintResult>;
}

export type MintResult =
  | { success: true; actionLink: string; targetEmail: string; expiresInSeconds: number }
  | { success: false; error: string };

const Ctx = createContext<ImpersonationValue | null>(null);

function readInitial(): { id: string | null; label: string | null } {
  if (typeof sessionStorage === 'undefined') return { id: null, label: null };
  try {
    return {
      id: sessionStorage.getItem(STORAGE_KEY),
      label: sessionStorage.getItem(LABEL_KEY),
    };
  } catch {
    return { id: null, label: null };
  }
}

/** Best-effort audit emit. Failures are swallowed — the admin shouldn't
 *  be blocked from setting / clearing the preview by an audit-write
 *  hiccup. Calls the existing admin_log_action RPC; if the action name
 *  is rejected because admin_audit_log's action column has a check
 *  constraint, the call returns an error and we proceed regardless. */
async function emitAudit(action: string, userId: string | null, label: string | null) {
  try {
    await callRpc<string>('admin_log_action', {
      _action: action,
      _target: userId,
      _metadata: { label, source: 'impersonation-stub' },
    });
  } catch {
    /* ignore */
  }
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const initial = readInitial();
  const [viewAsUserId, setViewAsUserId] = useState<string | null>(initial.id);
  const [label, setLabel] = useState<string | null>(initial.label);

  const start = useCallback(async (userId: string, lbl: string | null) => {
    setViewAsUserId(userId);
    setLabel(lbl);
    try {
      sessionStorage.setItem(STORAGE_KEY, userId);
      if (lbl) sessionStorage.setItem(LABEL_KEY, lbl);
      else sessionStorage.removeItem(LABEL_KEY);
    } catch { /* private mode */ }
    await emitAudit('admin.impersonation.start', userId, lbl);
  }, []);

  const stop = useCallback(async () => {
    const prevId = viewAsUserId;
    const prevLabel = label;
    setViewAsUserId(null);
    setLabel(null);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(LABEL_KEY);
    } catch { /* private mode */ }
    if (prevId) await emitAudit('admin.impersonation.stop', prevId, prevLabel);
  }, [viewAsUserId, label]);

  // Defence-in-depth: clear impersonation on tab visibility loss past
  // 60 minutes. Stops a long-running tab from silently staying in
  // impersonated state.
  useEffect(() => {
    if (!viewAsUserId) return;
    const started = Date.now();
    const id = window.setInterval(() => {
      if (Date.now() - started > 60 * 60 * 1000) {
        void stop();
      }
    }, 60_000);
    return () => window.clearInterval(id);
  }, [viewAsUserId, stop]);

  const mintImpersonationLink = useCallback(
    async (
      userId: string,
      opts?: { reason?: string; redirectTo?: string },
    ): Promise<MintResult> => {
      try {
        const { data, error } = await supabase.functions.invoke('admin-impersonate', {
          body: {
            target_user_id: userId,
            reason: opts?.reason,
            redirect_to: opts?.redirectTo,
          },
        });
        if (error) {
          return { success: false, error: error.message || 'mint-failed' };
        }
        if (!data?.success || !data.action_link) {
          return { success: false, error: data?.error ?? 'mint-failed' };
        }
        return {
          success: true,
          actionLink: data.action_link,
          targetEmail: data.target_email,
          expiresInSeconds: data.expires_in_seconds ?? 60,
        };
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : String(e) };
      }
    },
    [],
  );

  const value = useMemo<ImpersonationValue>(
    () => ({ viewAsUserId, label, start, stop, mintImpersonationLink }),
    [viewAsUserId, label, start, stop, mintImpersonationLink],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useImpersonation(): ImpersonationValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useImpersonation must be used inside <ImpersonationProvider>');
  return v;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useImpersonationOptional(): ImpersonationValue | null {
  return useContext(Ctx);
}
